import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { 
  MenuItem, 
  Select, 
  Typography, 
  Button, 
  Box, 
  FormControl, 
  InputLabel, 
  Slider, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Grid, 
  Card, 
  Divider 
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MidiPlayer from 'midi-player-js';

const MidiPlayerPage = () => {
  const [midiFiles, setMidiFiles] = useState([]);
  const [selectedMidiFile, setSelectedMidiFile] = useState(null);
  const [selectedInstrument, setSelectedInstrument] = useState('synth');
  const [midiFilesError, setMidiFilesError] = useState(null);
  const [midiFilesLoading, setMidiFilesLoading] = useState(true);
  const [toneModule, setToneModule] = useState(null);
  
  // Instrument parameter state management
  const [instrumentParams, setInstrumentParams] = useState({
    // Envelope parameters
    attack: 0.005,
    decay: 0.1,
    sustain: 0.3,
    release: 0.8,
    
    // Oscillator parameters
    oscillatorType: 'triangle',
    
    // Volume
    volume: -12,
    
    // Effects parameters
    reverbDecay: 1.8,
    reverbWet: 0.25,
    reverbPreDelay: 0.01,
    
    delayTime: 0.125, // 8th note
    delayFeedback: 0.2,
    delayWet: 0.1,
    
    eqLow: 0,
    eqMid: 0,
    eqHigh: 0,
    
    compressorThreshold: -20,
    compressorRatio: 4,
    
    // Polyphony
    maxPolyphony: 32
  });
  
  const [showParameterControls, setShowParameterControls] = useState(false);
  
  const playerRef = useRef(null);
  const synthsRef = useRef({});
  const currentInstrumentRef = useRef(null); // Single instrument reference
  const activeNotesRef = useRef(new Set()); // Track active notes for cleanup
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const loadTone = async () => {
      try {
        const toneImport = await import('tone/build/Tone.js');
        const resolvedTone = toneImport.default || toneImport;

        if (isMounted) {
          setToneModule(resolvedTone);
        }
      } catch (error) {
        console.error('Failed to load Tone.js:', error);
        if (isMounted) {
          setMidiFilesError('Failed to load the audio engine. Please refresh the page and try again.');
        }
      }
    };

    loadTone();

    return () => {
      isMounted = false;
    };
  }, []);

  const getToneModule = () => {
    if (!toneModule) {
      throw new Error('Tone.js is still loading. Please wait a moment and try again.');
    }

    return toneModule;
  };

  // Default parameter presets for each instrument
  const getInstrumentDefaults = (instrumentType) => {
    const presets = {
      synth: {
        attack: 0.005,
        decay: 0.1,
        sustain: 0.3,
        release: 0.8,
        oscillatorType: 'triangle',
        volume: -12,
        reverbDecay: 1.0,
        reverbWet: 0.1,
        reverbPreDelay: 0.01,
        delayTime: 0.125,
        delayFeedback: 0.1,
        delayWet: 0.05,
        eqLow: 0,
        eqMid: 0,
        eqHigh: 0,
        compressorThreshold: -20,
        compressorRatio: 2,
        maxPolyphony: 32
      },
      piano: {
        attack: 0.002,
        decay: 0.3,
        sustain: 0.05,
        release: 1.2,
        oscillatorType: 'custom',
        volume: -6,
        reverbDecay: 1.8,
        reverbWet: 0.25,
        reverbPreDelay: 0.01,
        delayTime: 0.125,
        delayFeedback: 0.0,
        delayWet: 0.0,
        eqLow: 2,
        eqMid: 0,
        eqHigh: -2,
        compressorThreshold: -20,
        compressorRatio: 4,
        maxPolyphony: 64
      },
      organ: {
        attack: 0.01,
        decay: 0.2,
        sustain: 0.8,
        release: 0.3,
        oscillatorType: 'sine4',
        volume: -10,
        reverbDecay: 2.0,
        reverbWet: 0.15,
        reverbPreDelay: 0.02,
        delayTime: 0.125,
        delayFeedback: 0.0,
        delayWet: 0.0,
        eqLow: 0,
        eqMid: 1,
        eqHigh: 0,
        compressorThreshold: -15,
        compressorRatio: 3,
        maxPolyphony: 32
      },
      strings: {
        attack: 0.2,
        decay: 0.1,
        sustain: 0.7,
        release: 1.5,
        oscillatorType: 'sawtooth',
        volume: -14,
        reverbDecay: 3.0,
        reverbWet: 0.4,
        reverbPreDelay: 0.02,
        delayTime: 0.125,
        delayFeedback: 0.1,
        delayWet: 0.05,
        eqLow: -1,
        eqMid: 0,
        eqHigh: 1,
        compressorThreshold: -25,
        compressorRatio: 2,
        maxPolyphony: 32
      },
      brass: {
        attack: 0.05,
        decay: 0.2,
        sustain: 0.6,
        release: 1.0,
        oscillatorType: 'square',
        volume: -8,
        reverbDecay: 1.5,
        reverbWet: 0.2,
        reverbPreDelay: 0.01,
        delayTime: 0.125,
        delayFeedback: 0.0,
        delayWet: 0.0,
        eqLow: 1,
        eqMid: 2,
        eqHigh: -1,
        compressorThreshold: -18,
        compressorRatio: 3,
        maxPolyphony: 16
      },
      flute: {
        attack: 0.1,
        decay: 0.2,
        sustain: 0.5,
        release: 1.2,
        oscillatorType: 'sine2',
        volume: -10,
        reverbDecay: 2.5,
        reverbWet: 0.3,
        reverbPreDelay: 0.02,
        delayTime: 0.125,
        delayFeedback: 0.15,
        delayWet: 0.1,
        eqLow: -2,
        eqMid: 0,
        eqHigh: 3,
        compressorThreshold: -22,
        compressorRatio: 2,
        maxPolyphony: 8
      },
      guitar: {
        attack: 0.01,
        decay: 0.4,
        sustain: 0.1,
        release: 1.5,
        oscillatorType: 'triangle',
        volume: -8,
        reverbDecay: 1.2,
        reverbWet: 0.15,
        reverbPreDelay: 0.01,
        delayTime: 0.125,
        delayFeedback: 0.2,
        delayWet: 0.1,
        eqLow: 1,
        eqMid: 0,
        eqHigh: 1,
        compressorThreshold: -16,
        compressorRatio: 3,
        maxPolyphony: 6
      }
    };
    
    return presets[instrumentType] || presets.synth;
  };

  // Function to load preset parameters for an instrument
  const loadInstrumentPreset = useCallback((instrumentType) => {
    const presetParams = getInstrumentDefaults(instrumentType);
    console.log(`🎼 Loading preset for ${instrumentType}:`, presetParams);
    setInstrumentParams(presetParams);
  }, []);

  useEffect(() => {
    fetchMidiFiles();
    // Load default preset for the initial instrument
    loadInstrumentPreset(selectedInstrument);
    return () => {
      // Cleanup on unmount
      if (playerRef.current) {
        playerRef.current.stop();
      }
      // Dispose of current instrument
      if (currentInstrumentRef.current) {
        currentInstrumentRef.current.dispose();
      }
      // Clean up any remaining individual synths
      Object.values(synthsRef.current).forEach(synth => {
        if (synth && synth.dispose) {
          synth.dispose();
        }
      });
      synthsRef.current = {};
      // Copy ref value to avoid stale closure
      const activeNotes = activeNotesRef.current;
      activeNotes.clear();
    };
  }, [loadInstrumentPreset, selectedInstrument]);

  const fetchMidiFiles = async () => {
    try {
      setMidiFilesLoading(true);
      setMidiFilesError(null);
      
      const response = await axiosInstance.get('/midi-files', {
        withCredentials: false // Temporarily disable credentials to avoid CORS issues
      });
      // Ensure response.data is an array
      if (Array.isArray(response.data)) {
        setMidiFiles(response.data);
        console.log('✅ Successfully loaded MIDI files from backend:', response.data.length);
      } else {
        console.warn('MIDI files response is not an array:', response.data);
        setMidiFiles([]);
      }
    } catch (error) {
      console.error('Error fetching MIDI files:', error);
      setMidiFilesError(error.message);
      
      // Ensure midiFiles is always an array even on error
      setMidiFiles([]);
      
      // Provide fallback local MIDI files for offline/demo use
      const fallbackFiles = [
        {
          _id: 'local-chopin',
          Name: 'Chopin Etude (Local)',
          Path: `${process.env.PUBLIC_URL || ''}/chopin_etude25_1.mid`
        },
        {
          _id: 'local-sustain',
          Name: 'Sustain Test (Local)', 
          Path: `${process.env.PUBLIC_URL || ''}/sustain_pedal_test.mid`
        }
      ];
      
      console.log('🎵 Using fallback local MIDI files:', fallbackFiles);
      setMidiFiles(fallbackFiles);
    } finally {
      setMidiFilesLoading(false);
    }
  };

  // Helper function to create appropriate instrument using current parameters
  const createInstrument = (instrumentType) => {
    const Tone = getToneModule();
    console.log(`🎼 Creating instrument: ${instrumentType} with parameters:`, instrumentParams);
    
    // Get oscillator type with proper handling for custom types
    const getOscillatorConfig = () => {
      if (instrumentParams.oscillatorType === 'custom' && instrumentType === 'piano') {
        return {
          type: "custom",
          partials: [1, 0.5, 0.3, 0.25, 0.2, 0.166, 0.14, 0.125, 0.111], // Piano-like harmonic series
        };
      }
      return { type: instrumentParams.oscillatorType };
    };

    // Create the base polyphonic synthesizer
    const synth = new Tone.PolySynth({
      maxPolyphony: instrumentParams.maxPolyphony,
      voice: Tone.Synth,
      options: {
        oscillator: getOscillatorConfig(),
        envelope: {
          attack: instrumentParams.attack,
          decay: instrumentParams.decay,
          sustain: instrumentParams.sustain,
          release: instrumentParams.release
        },
        volume: instrumentParams.volume
      }
    });

    // Create effects chain based on parameters
    const effects = [];
    
    // EQ (only add if not all zero)
    if (instrumentParams.eqLow !== 0 || instrumentParams.eqMid !== 0 || instrumentParams.eqHigh !== 0) {
      const eq = new Tone.EQ3({
        low: instrumentParams.eqLow,
        mid: instrumentParams.eqMid,
        high: instrumentParams.eqHigh
      });
      effects.push(eq);
    }
    
    // Compressor (only add if meaningful settings)
    if (instrumentParams.compressorRatio > 1) {
      const compressor = new Tone.Compressor({
        threshold: instrumentParams.compressorThreshold,
        ratio: instrumentParams.compressorRatio,
        attack: 0.003,
        release: 0.1
      });
      effects.push(compressor);
    }
    
    // Reverb (only add if wet > 0)
    if (instrumentParams.reverbWet > 0) {
      const reverb = new Tone.Reverb({
        decay: instrumentParams.reverbDecay,
        wet: instrumentParams.reverbWet,
        preDelay: instrumentParams.reverbPreDelay
      });
      effects.push(reverb);
    }
    
    // Delay (only add if wet > 0)
    if (instrumentParams.delayWet > 0) {
      const delay = new Tone.PingPongDelay({
        delayTime: instrumentParams.delayTime,
        feedback: instrumentParams.delayFeedback,
        wet: instrumentParams.delayWet
      });
      effects.push(delay);
    }

    // Chain effects together
    if (effects.length > 0) {
      synth.chain(...effects, Tone.Destination);
    } else {
      synth.toDestination();
    }

    console.log(`✅ Created ${instrumentType} with ${effects.length} effects:`, effects.map(e => e.constructor.name));
    return synth;
  };

  // Function to stop all active notes
  const stopAllActiveNotes = () => {
    console.log('🛑 Stopping all active notes...');
    let Tone;

    try {
      Tone = getToneModule();
    } catch (error) {
      console.warn('Tone.js not ready during note cleanup:', error.message);
    }
    
    // Stop all notes with current instrument
    if (currentInstrumentRef.current) {
      try {
        // Use releaseAll() if available, otherwise trigger release for each active note
        if (typeof currentInstrumentRef.current.releaseAll === 'function') {
          currentInstrumentRef.current.releaseAll();
        } else {
          // Fallback: trigger release for each tracked note
          activeNotesRef.current.forEach(noteKey => {
            const noteName = noteKey.split('-')[0];
            try {
              if (!Tone) {
                return;
              }
              currentInstrumentRef.current.triggerRelease(noteName, Tone.now());
            } catch (e) {
              console.warn('Failed to release note:', noteName, e);
            }
          });
        }
      } catch (error) {
        console.warn('Error stopping notes with current instrument:', error);
      }
    }
    
    // Clear the active notes tracking
    activeNotesRef.current.clear();
    console.log('✅ All active notes stopped and cleared');
  };

  // Function to update a specific instrument parameter
  const updateInstrumentParameter = (paramName, value) => {
    console.log(`🎛️ Updating parameter ${paramName} to ${value}`);
    setInstrumentParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  // Function to recreate instrument with current parameters
  const recreateInstrument = () => {
    console.log('🔄 Recreating instrument with current parameters...');
    
    // Stop all notes first
    stopAllActiveNotes();
    
    // Dispose of current instrument
    if (currentInstrumentRef.current) {
      currentInstrumentRef.current.dispose();
      currentInstrumentRef.current = null;
    }
    
    // Create new instrument with current parameters
    currentInstrumentRef.current = createInstrument(selectedInstrument);
    console.log('✅ Instrument recreated successfully');
  };

  const handleMidiFileChange = async (event) => {
    const selectedFile = midiFiles.find(file => file._id === event.target.value);
    setSelectedMidiFile(selectedFile);

    if (selectedFile) {
      try {
        const Tone = getToneModule();

        // Stop the current player if it exists
        if (playerRef.current) {
          playerRef.current.stop();
        }

        console.log('Loading MIDI file:', selectedFile.Name, selectedFile.Path);

        let midiData;
        
        // Check if it's a local file or remote file
        if (selectedFile.Path.startsWith('http') || selectedFile.Path.includes('blob.core.windows.net')) {
          // Remote Azure blob file - use backend proxy for secure access
          console.log('🎵 Fetching MIDI file via backend proxy...');
          
          try {
            // Extract filename from the Azure blob URL
            const filename = selectedFile.Path.split('/').pop().split('?')[0]; // Remove query params
            console.log(`🎵 Using backend proxy for file: ${filename}`);
            
            // Use backend proxy endpoint for secure access
            const response = await axiosInstance.get(`/midi-files/download/${encodeURIComponent(filename)}`, {
              responseType: 'arraybuffer',
              timeout: 30000, // 30 second timeout for file downloads
              withCredentials: false // Temporarily disable credentials to avoid CORS issues
            });
            
            if (response.data) {
              console.log('✅ Backend proxy download successful!');
              midiData = response.data;
            } else {
              throw new Error('No data received from backend proxy');
            }
            
          } catch (proxyError) {
            console.warn('❌ Backend proxy failed:', proxyError.message);
            
            // Fallback to local MIDI files
            console.log('🔄 Falling back to local MIDI files...');
            
            const knownMidiFiles = [
              'chopin_etude25_1.mid',
              'sustain_pedal_test.mid'
            ];
            
            let midiDataFetched = false;
            for (const midiFile of knownMidiFiles) {
              try {
                const localPath = `${process.env.PUBLIC_URL}/${midiFile}`;
                console.log('🎵 Trying local MIDI file:', localPath);
                const response = await fetch(localPath);
                if (response.ok) {
                  console.log('✅ Local MIDI file loaded successfully:', midiFile);
                  midiData = await response.arrayBuffer();
                  midiDataFetched = true;
                  
                  // Update the selected file display to show we're using a fallback
                  setSelectedMidiFile({
                    ...selectedFile,
                    Name: `${selectedFile.Name} (using local: ${midiFile})`
                  });
                  break;
                }
              } catch (err) {
                console.log('❌ Local file failed:', midiFile, err.message);
              }
            }
            
            if (!midiDataFetched) {
              throw new Error(`Backend proxy failed and no local fallback files available. Error: ${proxyError.message}`);
            }
          }
        } else {
          // Local file - try direct fetch or use public folder fallback
          console.log('Fetching local MIDI file from:', selectedFile.Path);
          
          let response;
          try {
            response = await fetch(selectedFile.Path);
          } catch (fetchError) {
            // Fallback to public folder MIDI files
            console.warn('Direct fetch failed, trying public folder fallback');
            const publicPath = `${process.env.PUBLIC_URL}/${selectedFile.Name.toLowerCase().replace(/\s+/g, '_')}.mid`;
            console.log('Trying fallback path:', publicPath);
            response = await fetch(publicPath);
          }
          
          if (!response.ok) {
            // Try another fallback - use available public MIDI files
            console.warn('Fallback failed, trying known public MIDI files');
            const knownMidiFiles = [
              'chopin_etude25_1.mid',
              'sustain_pedal_test.mid'
            ];
            
            let foundFile = false;
            for (const midiFile of knownMidiFiles) {
              try {
                const testPath = `${process.env.PUBLIC_URL}/${midiFile}`;
                console.log('Trying known MIDI file:', testPath);
                response = await fetch(testPath);
                if (response.ok) {
                  console.log('Successfully found working MIDI file:', midiFile);
                  foundFile = true;
                  break;
                }
              } catch (err) {
                console.log('Failed to load', midiFile, ':', err.message);
              }
            }
            
            if (!foundFile) {
              throw new Error(`Failed to fetch any MIDI file. Last attempt: ${response.status} ${response.statusText}`);
            }
          }
          
          midiData = await response.arrayBuffer();
        }

        // Validate MIDI file format
        const uint8Array = new Uint8Array(midiData);
        console.log('MIDI file size:', uint8Array.length, 'bytes');
        console.log('First 4 bytes:', Array.from(uint8Array.slice(0, 4)).map(b => String.fromCharCode(b)).join(''));
        
        // Check for valid MIDI header
        if (uint8Array.length < 4) {
          throw new Error('MIDI file is too small');
        }
        
        const header = String.fromCharCode(...uint8Array.slice(0, 4));
        if (header !== 'MThd') {
          console.error('Invalid MIDI header. Expected "MThd", got:', header);
          console.error('File bytes (hex):', Array.from(uint8Array.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' '));
          throw new Error(`Invalid MIDI file format. Expected "MThd" header, got "${header}"`);
        }

        console.log('Valid MIDI file detected, creating player...');
        
        // Create the MIDI player
        const player = new MidiPlayer.Player((event) => {
          // Handle MIDI events with improved synthesis
          if (event.name === 'Note on' && event.velocity > 0) {
            console.log(`Playing note: ${event.noteName} with velocity: ${event.velocity}`);
            
            // Track active note
            const noteKey = `${event.noteName}-${event.noteNumber}`;
            activeNotesRef.current.add(noteKey);
            
            // Create instrument if it doesn't exist
            if (!currentInstrumentRef.current) {
              currentInstrumentRef.current = createInstrument(selectedInstrument);
              console.log(`✅ ${selectedInstrument} instrument created successfully`);
            }
            
            // Use polyphonic triggering with velocity scaling
            const velocity = Math.max(0.05, Math.min(1.0, event.velocity / 127));
            
            // Enhanced velocity response for piano
            let adjustedVelocity = velocity;
            if (selectedInstrument === 'piano') {
              // Piano-specific velocity curve for more realistic dynamics
              adjustedVelocity = Math.pow(velocity, 0.7); // Softer velocity curve
              adjustedVelocity = Math.max(0.1, Math.min(0.9, adjustedVelocity));
            }
            
            currentInstrumentRef.current.triggerAttack(event.noteName, Tone.now(), adjustedVelocity);
            
          } else if (event.name === 'Note off' || (event.name === 'Note on' && event.velocity === 0)) {
            console.log(`Stopping note: ${event.noteName}`);
            
            // Remove from active notes
            const noteKey = `${event.noteName}-${event.noteNumber}`;
            activeNotesRef.current.delete(noteKey);
            
            if (currentInstrumentRef.current) {
              currentInstrumentRef.current.triggerRelease(event.noteName, Tone.now());
            }
          }
        });

        // Stop all active notes when playback ends
        player.on('endOfFile', () => {
          console.log('Playback ended. Stopping all active notes.');

          // Add a small delay to ensure the last events are processed
          setTimeout(() => {
            // Stop all notes using the improved cleanup
            stopAllActiveNotes();
            console.log('All active notes stopped.');
          }, 100); // 100ms delay
        });

        console.log('Loading MIDI data into player...');
        player.loadArrayBuffer(midiData);
        playerRef.current = player;

        // Ensure the Tone.js context is started
        if (Tone.context.state !== 'running') {
          console.log('🔊 Starting Tone.js audio context...');
          await Tone.start();
          console.log('✅ Tone.js context started');
        } else {
          console.log('✅ Tone.js context already running');
        }

        console.log('Starting MIDI playback...');
        playerRef.current.play();
        
      } catch (error) {
        console.error('Error loading MIDI file:', error);
        
        // Enhanced error message for users
        let userMessage = `Error loading MIDI file: ${error.message}`;
        
        if (error.message.includes('403') || error.message.includes('not authorized')) {
          userMessage = `🔐 Azure storage access denied (SAS token expired). Attempting to use local MIDI files instead...\n\nOriginal error: ${error.message}`;
          
          // Try immediate fallback to local files
          try {
            console.log('🎵 Attempting emergency fallback to local MIDI files...');
            const Tone = getToneModule();
            const localFile = `${process.env.PUBLIC_URL}/chopin_etude25_1.mid`;
            const response = await fetch(localFile);
            
            if (response.ok) {
              const localMidiData = await response.arrayBuffer();
              const uint8Array = new Uint8Array(localMidiData);
              
              if (uint8Array.length >= 4 && String.fromCharCode(...uint8Array.slice(0, 4)) === 'MThd') {
                console.log('✅ Emergency fallback successful! Loading Chopin Etude...');
                
                const player = new MidiPlayer.Player((event) => {
                  // Use same improved event handling as main player
                  if (event.name === 'Note on' && event.velocity > 0) {
                    console.log(`Emergency fallback - Playing note: ${event.noteName} with velocity: ${event.velocity}`);
                    
                    const noteKey = `${event.noteName}-${event.noteNumber}`;
                    activeNotesRef.current.add(noteKey);
                    
                    // Create instrument if it doesn't exist using the unified system
                    if (!currentInstrumentRef.current) {
                      console.log(`🎵 Emergency fallback - Creating ${selectedInstrument} instrument...`);
                      currentInstrumentRef.current = createInstrument(selectedInstrument);
                    }
                    
                    // Use polyphonic triggering with velocity scaling
                    const velocity = Math.max(0.05, Math.min(1.0, event.velocity / 127));
                    
                    // Enhanced velocity response for piano
                    let adjustedVelocity = velocity;
                    if (selectedInstrument === 'piano') {
                      // Piano-specific velocity curve for more realistic dynamics
                      adjustedVelocity = Math.pow(velocity, 0.7); // Softer velocity curve
                      adjustedVelocity = Math.max(0.1, Math.min(0.9, adjustedVelocity));
                    }
                    
                    currentInstrumentRef.current.triggerAttack(event.noteName, Tone.now(), adjustedVelocity);
                    
                  } else if (event.name === 'Note off' || (event.name === 'Note on' && event.velocity === 0)) {
                    console.log(`Emergency fallback - Stopping note: ${event.noteName}`);
                    
                    const noteKey = `${event.noteName}-${event.noteNumber}`;
                    activeNotesRef.current.delete(noteKey);
                    
                    if (currentInstrumentRef.current) {
                      currentInstrumentRef.current.triggerRelease(event.noteName, Tone.now());
                    }
                  }
                });

                player.loadArrayBuffer(localMidiData);
                playerRef.current = player;
                
                if (Tone.context.state !== 'running') {
                  await Tone.start();
                }
                player.play();
                
                // Update display
                setSelectedMidiFile({
                  ...selectedFile,
                  Name: `Emergency Fallback: Chopin Etude (Local File)`
                });
                
                userMessage = `✅ Successfully loaded local MIDI file as fallback!\n\n🎵 Now playing: Chopin Etude 25 No. 1\n\n💡 To fix Azure access, update the SAS token in the code.`;
              }
            }
          } catch (fallbackError) {
            console.error('❌ Emergency fallback also failed:', fallbackError);
            userMessage += `\n\n❌ Local fallback also failed: ${fallbackError.message}`;
          }
        }
        
        alert(userMessage);
      }
    }
  };

  const handleInstrumentChange = (event) => {
    const newInstrument = event.target.value;
    setSelectedInstrument(newInstrument);
    
    // Stop all current notes before switching
    stopAllActiveNotes();
    
    // Dispose of old instrument
    if (currentInstrumentRef.current) {
      currentInstrumentRef.current.dispose();
      currentInstrumentRef.current = null;
    }
    
    // Load preset parameters for the new instrument
    loadInstrumentPreset(newInstrument);
    
    console.log(`🎼 Switched to ${newInstrument} instrument`);
  };

  const handleStop = () => {
    if (playerRef.current) {
      playerRef.current.stop();
    }
    stopAllActiveNotes();
  };

  const handlePause = () => {
    if (playerRef.current && playerRef.current.isPlaying()) {
      playerRef.current.pause();
      stopAllActiveNotes(); // Stop any hanging notes when pausing
    }
  };

  const handleResume = async () => {
    try {
      const Tone = getToneModule();
      if (playerRef.current) {
        // Ensure Tone.js audio context is started
        if (Tone.context.state !== 'running') {
          console.log('🔊 Starting Tone.js audio context...');
          await Tone.start();
          console.log('✅ Tone.js audio context started successfully');
        }
        playerRef.current.play();
      }
    } catch (error) {
      console.error('Error starting audio playback:', error);
      alert('Unable to start audio. Please make sure you have clicked on the page first, then try again.');
    }
  };

  // Handle cleanup when the component unmounts
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.stop();
      }
      if (currentInstrumentRef.current) {
        currentInstrumentRef.current.dispose();
      }
    };
  }, [playerRef, currentInstrumentRef]);
  
  // Navigation handler
  const handleBackToMain = () => {
    navigate('/');
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        🎹 MIDI Player
      </Typography>
      
      {midiFilesError && (
        <Box mb={2} p={2} bgcolor="warning.light" borderRadius={1}>
          <Typography variant="body2" color="textSecondary">
            ⚠️ Unable to connect to backend server. Using local MIDI files for demo.
            <br />
            <small>Error: {midiFilesError}</small>
          </Typography>
        </Box>
      )}
      
      <Box mb={2}>
        <FormControl margin="dense" style={{ minWidth: 200, marginRight: '10px' }}>
          <InputLabel>Select MIDI File</InputLabel>
          <Select
            value={selectedMidiFile ? selectedMidiFile._id : ''}
            onChange={handleMidiFileChange}
            disabled={midiFilesLoading || !toneModule}
          >
            {midiFilesLoading ? (
              <MenuItem disabled>Loading MIDI files...</MenuItem>
            ) : !toneModule ? (
              <MenuItem disabled>Loading audio engine...</MenuItem>
            ) : midiFiles && Array.isArray(midiFiles) && midiFiles.length > 0 ? (
              midiFiles.map((file) => (
                <MenuItem key={file._id} value={file._id}>
                  {file.Name}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No MIDI files available</MenuItem>
            )}
          </Select>
        </FormControl>

        <FormControl margin="dense" style={{ minWidth: 150, marginRight: '10px' }}>
          <InputLabel>Instrument</InputLabel>
          <Select
            value={selectedInstrument}
            onChange={handleInstrumentChange}
          >
            <MenuItem value="synth">🎵 Synth</MenuItem>
            <MenuItem value="piano">🎹 Piano</MenuItem>
            <MenuItem value="organ">🎭 Organ</MenuItem>
            <MenuItem value="strings">🎻 Strings</MenuItem>
            <MenuItem value="brass">🎺 Brass</MenuItem>
            <MenuItem value="flute">🪈 Flute</MenuItem>
            <MenuItem value="guitar">🎸 Guitar</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box mb={2}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleResume}
          style={{ marginRight: '10px' }}
        >
          ▶️ Play
        </Button>
        <Button 
          variant="contained" 
          color="secondary" 
          onClick={handlePause}
          style={{ marginRight: '10px' }}
        >
          ⏸️ Pause
        </Button>
        <Button 
          variant="contained" 
          color="error" 
          onClick={handleStop}
          style={{ marginRight: '10px' }}
        >
          ⏹️ Stop
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => setShowParameterControls(!showParameterControls)}
          style={{ marginRight: '10px' }}
        >
          🎛️ {showParameterControls ? 'Hide' : 'Show'} Controls
        </Button>
        <Button 
          variant="outlined" 
          onClick={recreateInstrument}
          style={{ marginRight: '10px' }}
        >
          🔄 Apply Changes
        </Button>

      </Box>

      {/* Instrument Parameter Controls */}
      {showParameterControls && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            🎛️ Instrument Parameters - {selectedInstrument.charAt(0).toUpperCase() + selectedInstrument.slice(1)}
          </Typography>
          
          <Grid container spacing={3}>
            {/* Envelope Controls */}
            <Grid item xs={12} md={6}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">📊 Envelope (ADSR)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography gutterBottom>Attack: {instrumentParams.attack.toFixed(3)}s</Typography>
                    <Slider
                      value={instrumentParams.attack}
                      onChange={(_, value) => updateInstrumentParameter('attack', value)}
                      min={0.001}
                      max={2}
                      step={0.001}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Decay: {instrumentParams.decay.toFixed(3)}s</Typography>
                    <Slider
                      value={instrumentParams.decay}
                      onChange={(_, value) => updateInstrumentParameter('decay', value)}
                      min={0.01}
                      max={2}
                      step={0.01}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Sustain: {instrumentParams.sustain.toFixed(2)}</Typography>
                    <Slider
                      value={instrumentParams.sustain}
                      onChange={(_, value) => updateInstrumentParameter('sustain', value)}
                      min={0}
                      max={1}
                      step={0.01}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Release: {instrumentParams.release.toFixed(3)}s</Typography>
                    <Slider
                      value={instrumentParams.release}
                      onChange={(_, value) => updateInstrumentParameter('release', value)}
                      min={0.01}
                      max={5}
                      step={0.01}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Oscillator & Volume Controls */}
            <Grid item xs={12} md={6}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">🌊 Oscillator & Volume</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Oscillator Type</InputLabel>
                      <Select
                        value={instrumentParams.oscillatorType}
                        onChange={(e) => updateInstrumentParameter('oscillatorType', e.target.value)}
                      >
                        <MenuItem value="sine">Sine</MenuItem>
                        <MenuItem value="square">Square</MenuItem>
                        <MenuItem value="sawtooth">Sawtooth</MenuItem>
                        <MenuItem value="triangle">Triangle</MenuItem>
                        <MenuItem value="sine2">Sine2</MenuItem>
                        <MenuItem value="sine4">Sine4</MenuItem>
                        {selectedInstrument === 'piano' && <MenuItem value="custom">Piano (Custom)</MenuItem>}
                      </Select>
                    </FormControl>
                    
                    <Typography gutterBottom>Volume: {instrumentParams.volume}dB</Typography>
                    <Slider
                      value={instrumentParams.volume}
                      onChange={(_, value) => updateInstrumentParameter('volume', value)}
                      min={-30}
                      max={0}
                      step={1}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Max Polyphony: {instrumentParams.maxPolyphony}</Typography>
                    <Slider
                      value={instrumentParams.maxPolyphony}
                      onChange={(_, value) => updateInstrumentParameter('maxPolyphony', value)}
                      min={1}
                      max={128}
                      step={1}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Reverb Controls */}
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">🏠 Reverb</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography gutterBottom>Decay: {instrumentParams.reverbDecay.toFixed(2)}s</Typography>
                    <Slider
                      value={instrumentParams.reverbDecay}
                      onChange={(_, value) => updateInstrumentParameter('reverbDecay', value)}
                      min={0.1}
                      max={10}
                      step={0.1}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Wet: {(instrumentParams.reverbWet * 100).toFixed(0)}%</Typography>
                    <Slider
                      value={instrumentParams.reverbWet}
                      onChange={(_, value) => updateInstrumentParameter('reverbWet', value)}
                      min={0}
                      max={1}
                      step={0.01}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Pre-Delay: {instrumentParams.reverbPreDelay.toFixed(3)}s</Typography>
                    <Slider
                      value={instrumentParams.reverbPreDelay}
                      onChange={(_, value) => updateInstrumentParameter('reverbPreDelay', value)}
                      min={0}
                      max={0.1}
                      step={0.001}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Delay Controls */}
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">⏱️ Delay</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography gutterBottom>Delay Time: {instrumentParams.delayTime.toFixed(3)}s</Typography>
                    <Slider
                      value={instrumentParams.delayTime}
                      onChange={(_, value) => updateInstrumentParameter('delayTime', value)}
                      min={0.01}
                      max={1}
                      step={0.01}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Feedback: {(instrumentParams.delayFeedback * 100).toFixed(0)}%</Typography>
                    <Slider
                      value={instrumentParams.delayFeedback}
                      onChange={(_, value) => updateInstrumentParameter('delayFeedback', value)}
                      min={0}
                      max={0.9}
                      step={0.01}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Wet: {(instrumentParams.delayWet * 100).toFixed(0)}%</Typography>
                    <Slider
                      value={instrumentParams.delayWet}
                      onChange={(_, value) => updateInstrumentParameter('delayWet', value)}
                      min={0}
                      max={1}
                      step={0.01}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* EQ Controls */}
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">🎚️ Equalizer</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography gutterBottom>Low: {instrumentParams.eqLow > 0 ? '+' : ''}{instrumentParams.eqLow.toFixed(1)}dB</Typography>
                    <Slider
                      value={instrumentParams.eqLow}
                      onChange={(_, value) => updateInstrumentParameter('eqLow', value)}
                      min={-12}
                      max={12}
                      step={0.1}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Mid: {instrumentParams.eqMid > 0 ? '+' : ''}{instrumentParams.eqMid.toFixed(1)}dB</Typography>
                    <Slider
                      value={instrumentParams.eqMid}
                      onChange={(_, value) => updateInstrumentParameter('eqMid', value)}
                      min={-12}
                      max={12}
                      step={0.1}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>High: {instrumentParams.eqHigh > 0 ? '+' : ''}{instrumentParams.eqHigh.toFixed(1)}dB</Typography>
                    <Slider
                      value={instrumentParams.eqHigh}
                      onChange={(_, value) => updateInstrumentParameter('eqHigh', value)}
                      min={-12}
                      max={12}
                      step={0.1}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>

            {/* Compressor Controls */}
            <Grid item xs={12} md={6}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">🗜️ Compressor</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box>
                    <Typography gutterBottom>Threshold: {instrumentParams.compressorThreshold}dB</Typography>
                    <Slider
                      value={instrumentParams.compressorThreshold}
                      onChange={(_, value) => updateInstrumentParameter('compressorThreshold', value)}
                      min={-40}
                      max={0}
                      step={1}
                      sx={{ mb: 2 }}
                    />
                    
                    <Typography gutterBottom>Ratio: {instrumentParams.compressorRatio.toFixed(1)}:1</Typography>
                    <Slider
                      value={instrumentParams.compressorRatio}
                      onChange={(_, value) => updateInstrumentParameter('compressorRatio', value)}
                      min={1}
                      max={20}
                      step={0.1}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 2 }} />
          
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button 
              variant="outlined" 
              onClick={() => loadInstrumentPreset(selectedInstrument)}
            >
              🔄 Reset to Preset
            </Button>
            <Button 
              variant="contained" 
              onClick={recreateInstrument}
              color="primary"
            >
              ✅ Apply All Changes
            </Button>
          </Box>
        </Card>
      )}
      
      {selectedMidiFile && (
        <Box mt={2}>
          <Typography variant="h6">
            Now Playing: {selectedMidiFile.Name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Instrument: {selectedInstrument}
          </Typography>
        </Box>
      )}
      
      {/* Back to Main Page Button */}
      <Box mt={4} mb={2} display="flex" justifyContent="center">
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleBackToMain}
          sx={{ minWidth: '200px' }}
        >
          Back to Main Page
        </Button>
      </Box>
    </Box>
  );
};

export default MidiPlayerPage;
